import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "totalCount", "generateBtn", "summaryContent",
    "summaryLoading", "summaryBody", "fieldStats",
    "expandedList", "syncBtn"
  ]

  static values = {
    activityId: String,
    inloopId: String,
    outcomesUrl: String,
    generateUrl: String,
    fields: Array
  }

  connect() {
    this.fetchOutcomes()
  }

  async syncData() {
    if (this._syncing) return
    this._syncing = true

    if (this.hasSyncBtnTarget) {
      this.syncBtnTarget.disabled = true
      this.syncBtnTarget.querySelector("[data-sync-icon]")?.classList.add("animate-spin")
    }

    await this.fetchOutcomes()

    this._syncing = false
    if (this.hasSyncBtnTarget) {
      this.syncBtnTarget.disabled = false
      this.syncBtnTarget.querySelector("[data-sync-icon]")?.classList.remove("animate-spin")
    }
  }

  async fetchOutcomes() {
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(this.outcomesUrlValue, {
        headers: { "Accept": "application/json", "X-CSRF-Token": token }
      })
      if (!response.ok) return

      const data = await response.json()

      if (this.hasTotalCountTarget) {
        this.totalCountTarget.textContent = data.total_count
      }

      this.updateFieldStats(data.field_stats)

      if (data.ai_summary && this.hasSummaryBodyTarget) {
        this.renderSummary(data.ai_summary)
      }
    } catch (err) {
      console.error("Error fetching outcomes:", err)
    }
  }

  async generateSummary() {
    if (this._generating) return
    this._generating = true

    if (this.hasGenerateBtnTarget) {
      this.generateBtnTarget.disabled = true
      this.generateBtnTarget.classList.add("opacity-50")
    }
    if (this.hasSummaryLoadingTarget) {
      this.summaryLoadingTarget.classList.remove("hidden")
    }
    if (this.hasSummaryBodyTarget) {
      this.summaryBodyTarget.classList.add("hidden")
    }

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(this.generateUrlValue, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": token
        }
      })

      if (!response.ok) throw new Error("Generation failed")

      const data = await response.json()
      this.renderSummary(data.summary)
    } catch (err) {
      console.error("Error generating summary:", err)
      if (this.hasSummaryBodyTarget) {
        this.summaryBodyTarget.innerHTML = `<p class="text-sm text-error-500">Failed to generate summary. Please try again.</p>`
        this.summaryBodyTarget.classList.remove("hidden")
      }
    } finally {
      this._generating = false
      if (this.hasGenerateBtnTarget) {
        this.generateBtnTarget.disabled = false
        this.generateBtnTarget.classList.remove("opacity-50")
        this.generateBtnTarget.innerHTML = `<svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg> Regenerate`
      }
      if (this.hasSummaryLoadingTarget) {
        this.summaryLoadingTarget.classList.add("hidden")
      }
    }
  }

  renderSummary(summary) {
    if (!this.hasSummaryBodyTarget) return

    const findings = (summary.key_findings || []).map(f =>
      `<li class="flex items-start gap-2 text-sm text-gray-700">
        <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-500 flex-shrink-0"></span>
        ${this.esc(f)}
      </li>`
    ).join("")

    const recs = (summary.recommendations || []).map(r =>
      `<li class="flex items-start gap-2 text-sm text-gray-700">
        <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-success-500 flex-shrink-0"></span>
        ${this.esc(r)}
      </li>`
    ).join("")

    let timeAgo = ""
    if (summary.generated_at) {
      try {
        const date = new Date(summary.generated_at)
        const diff = Math.floor((Date.now() - date.getTime()) / 60000)
        if (diff < 1) timeAgo = "just now"
        else if (diff < 60) timeAgo = `${diff} min ago`
        else if (diff < 1440) timeAgo = `${Math.floor(diff / 60)} hours ago`
        else timeAgo = `${Math.floor(diff / 1440)} days ago`
      } catch { timeAgo = "" }
    }

    this.summaryBodyTarget.innerHTML = `
      <p class="text-sm text-gray-700 leading-relaxed mb-4">${this.esc(summary.overall_summary || "")}</p>
      ${findings ? `
        <div class="mb-4">
          <h4 class="text-xs font-semibold text-gray-500 uppercase mb-2">Key Findings</h4>
          <ul class="flex flex-col gap-1.5">${findings}</ul>
        </div>
      ` : ""}
      ${recs ? `
        <div>
          <h4 class="text-xs font-semibold text-gray-500 uppercase mb-2">Recommendations</h4>
          <ul class="flex flex-col gap-1.5">${recs}</ul>
        </div>
      ` : ""}
      ${timeAgo ? `<p class="text-xs text-gray-400 mt-4">Generated ${timeAgo} &middot; Based on ${summary.response_count_at_generation || 0} responses</p>` : ""}
    `
    this.summaryBodyTarget.classList.remove("hidden")
  }

  updateFieldStats(fieldStats) {
    if (!fieldStats) return

    fieldStats.forEach(stat => {
      const container = this.element.querySelector(`[data-stat-field-id="${stat.id}"]`)
      if (!container) return

      if (stat.options) {
        // select, multi_select, yes_no
        const bars = container.querySelectorAll(".flex.items-center.gap-3")
        stat.options.forEach((opt, i) => {
          if (bars[i]) {
            const progressBar = bars[i].querySelector("[style*='width']")
            const countSpan = bars[i].querySelector(".text-right")
            if (progressBar) progressBar.style.width = `${opt.pct}%`
            if (countSpan) countSpan.textContent = `${opt.count} (${opt.pct}%)`
          }
        })
      }
    })
  }

  toggleResponseList(e) {
    const btn = e.currentTarget
    const list = btn.nextElementSibling
    if (!list) return

    const isHidden = list.classList.contains("hidden")
    list.classList.toggle("hidden")
    btn.textContent = isHidden ? "Show less" : btn.textContent.replace("Show less", `Show all`)
  }

  esc(str) {
    if (!str) return ""
    const div = document.createElement("div")
    div.textContent = String(str)
    return div.innerHTML
  }
}
